import { useDispatch, useSelector } from "react-redux";
import { useToast } from "@chakra-ui/react";
import { constructQueryParamsString, showErrorToast } from "../../../utils";
import { useGetAllAudit, useGetQuestionnaireRounds, useGetQuestionsOfRound } from "../../../hooks/query";
import { setAudits, setQuestions, setRoundList, setSelectedRound } from "../../../context/Store/slices/issbRoundSlice";
import { useCallback, useEffect } from "react";
import useAirUserRoles from "../../../utils/AuthUtils";
import { useNavigate } from "react-router-dom";
import { routes } from "../../../config";

export const useRefreshRoundList = () => {
  const dispatch = useDispatch();
  const toast = useToast();
  const { isController } = useAirUserRoles();

  const getQueryParamsString = () => {
    const queryParams = {
      page: 0,
      size: 500,
    };
    return constructQueryParamsString(queryParams);
  };

  const { refetch } = useGetQuestionnaireRounds(getQueryParamsString(), {
    onSuccess: async (data) => {
      const pageContent = data?.content ?? [];
      // filter out round that are DRAFT and not controller
      const rounds = pageContent.filter((round) => {
        return !(round?.state === "DRAFT" && !isController(round));
      });
      dispatch(setRoundList(rounds));
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 1000 * 60 * 5,
  });

  const refreshRoundList = useCallback(async () => {
    await refetch();
  }, [refetch]);

  return { refreshRoundList };
};

export const useSelectRound = () => {
  const dispatch = useDispatch();
  const toast = useToast();
  const { isController } = useAirUserRoles();
  const navigate = useNavigate();

  return (round) => {
    if (round.state === "DRAFT" && !isController(round)) {
      showErrorToast(toast, "Only the controller can select a draft round");
      return;
    }
    dispatch(setSelectedRound(round));

    // show a toast that the round is changed and redirect to the dashboard
    toast({
      title: "Round Changed",
      description: `Redirecting to the dashboard ...`,
      status: "success",
      duration: 3000,
      isClosable: true,
    });
    setTimeout(() => {
      navigate(routes.issbDashboard);
    }, 1000);
  };
};

/** TODO doc the use-cases that need this, and the lifecycle of cached data
 * @deprecated Can we replace this DIY cache with useQuery() / useGetQuestionsOfRound() -- for clearer cache management and less disconnected state code?
 */
export const useSyncQuestionsWithSelectedRound = () => {
  const selectedRound = useSelector((state) => state.issbRound.selectedRound);
  const dispatch = useDispatch();
  const toast = useToast();

  const { refetch } = useGetQuestionsOfRound(null, selectedRound?.id, {
    enabled: false,
    onSuccess: (data) => {
      dispatch(setQuestions(data));
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
  });

  useEffect(() => {
    if (selectedRound?.id) {
      refetch();
    }
  }, [refetch, selectedRound?.id]);
};

/**
 * @deprecated See comments on useSyncQuestionsWithSelectedRound
 */
export const useSyncAuditsWithSelectedRound = () => {
  const selectedRound = useSelector((state) => state.issbRound.selectedRound);
  const dispatch = useDispatch();
  const toast = useToast();

  const { refetch } = useGetAllAudit(selectedRound?.id, {
    enabled: false,
    onSuccess: (data) => {
      dispatch(setAudits(data));
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
  });

  useEffect(() => {
    if (selectedRound?.id) {
      refetch();
    }
  }, [refetch, selectedRound?.id]);
};
